import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface RouteMeta {
  title: string;
  description: string;
  canonical: string;
  robots?: string;
}

const DEFAULT_ROBOTS = 'index,follow';

const ROUTES: Record<string, RouteMeta> = {
  '/': {
    title: 'Intent Data and Identity Resolution | Data Aviator',
    description: 'Data Aviator tracks intent across 17 million URLs and resolves it to a 308-million-person verified identity - know who\'s ready to buy before they visit your site.',
    canonical: 'https://data-aviator.com/',
  },
  '/why-our-data-is-accurate': {
    title: 'Why Our Data Is Accurate | Data Aviator',
    description: 'Deterministic identity resolution: up to 70% of website visitors identified against 308M verified profiles. Here is how it works and how we prove it.',
    canonical: 'https://data-aviator.com/why-our-data-is-accurate',
  },
  '/original': {
    title: 'Original Homepage (Internal) | Data Aviator',
    description: 'Internal archive of the previous Data Aviator homepage. Not indexed.',
    canonical: 'https://data-aviator.com/original',
    robots: 'noindex,nofollow',
  },
  '/where-the-data-comes-from': {
    title: 'Where Our Intent Data Comes From | Data Aviator',
    description: 'See exactly where your B2B intent data originates. Data Aviator combines 50+ verified, consent-based sources with rigorous validation for accuracy you can trust.',
    canonical: 'https://data-aviator.com/where-the-data-comes-from',
  },
  '/how-intent-data-is-gathered': {
    title: 'How Intent Data Is Gathered | Data Aviator',
    description: 'A behind-the-scenes look at how Data Aviator gathers, validates, and refreshes B2B intent data, so you can trust the signals before you act on them.',
    canonical: 'https://data-aviator.com/how-intent-data-is-gathered',
  },
  '/7-core-segments': {
    title: '7 Core Intent Data Segments and Filters | Data Aviator',
    description: 'Explore the core B2B data segments and filters in Data Aviator, with a data points reference for building precise, in-market audiences that convert.',
    canonical: 'https://data-aviator.com/7-core-segments',
  },
  '/capabilities': {
    title: 'B2B Intent Data Capabilities | Data Aviator',
    description: 'A one-page overview of Data Aviator capabilities: aggregated and validated B2B intent data, compliance-first design, and audiences ready to activate.',
    canonical: 'https://data-aviator.com/capabilities',
  },
  '/capabilities-onepager': {
    title: 'Data Aviator Capabilities One-Pager',
    description: 'Private capabilities one-pager for Data Aviator. Not indexed.',
    canonical: 'https://data-aviator.com/capabilities-onepager',
    robots: 'noindex,nofollow',
  },
  '/vs-apollo': {
    title: 'Data Aviator vs Apollo.io: Intent Data | Data Aviator',
    description: 'Compare Data Aviator and Apollo.io for B2B intent data. See how sourcing, verification, and deliverability differ before you choose a data provider.',
    canonical: 'https://data-aviator.com/vs-apollo',
  },
  '/pricing': {
    title: 'Intent Data Pricing for Real Buyers | Data Aviator',
    description: 'Data Aviator pricing is built for real buyers, not broad guesses. See plans for verified B2B intent data and audiences you can activate immediately.',
    canonical: 'https://data-aviator.com/pricing',
  },
  '/facebook-lookalike-audiences': {
    title: 'Facebook Lookalike Audiences That Convert | Data Aviator',
    description: 'Use verified B2B intent data to build Facebook lookalike audiences that convert. Feed cleaner seed lists into Meta Ads for sharper targeting and lower waste.',
    canonical: 'https://data-aviator.com/facebook-lookalike-audiences',
  },
  '/blog': {
    title: 'B2B Data and Intent Insights Blog | Data Aviator',
    description: 'Insights, updates, and industry trends in B2B data, intent signals, and prospecting. Practical guidance for marketers, sales, and RevOps from Data Aviator.',
    canonical: 'https://data-aviator.com/blog',
  },
  '/newsletter': {
    title: 'Newsletter: B2B Intent Insights | Data Aviator',
    description: 'Get exclusive B2B data and intent insights from Data Aviator delivered to your inbox. Intelligence that flies higher, with no fluff and no spam.',
    canonical: 'https://data-aviator.com/newsletter',
  },
  '/contact': {
    title: 'Schedule a Data Walkthrough | Data Aviator',
    description: 'Schedule a walkthrough with Data Aviator. Tell us what you need and we will show you the data and how to activate it for your campaigns. Test the data first.',
    canonical: 'https://data-aviator.com/contact',
  },
  '/auth': {
    title: 'Log In to Your Account | Data Aviator',
    description: 'Log in to your Data Aviator account to access your B2B intent data, audiences, and account settings.',
    canonical: 'https://data-aviator.com/auth',
    robots: 'noindex,follow',
  },
  '/opt-out': {
    title: 'Do Not Sell My Personal Information | Data Aviator',
    description: 'Submit a Do Not Sell My Personal Information request to Data Aviator and exercise your privacy rights under CCPA and applicable state laws.',
    canonical: 'https://data-aviator.com/opt-out',
  },
  '/data-source': {
    title: 'Data Source (Redirect) | Data Aviator',
    description: 'This URL is a legacy alias. The canonical page is /where-the-data-comes-from, which documents Data Aviator\'s 50+ verified B2B intent data sources.',
    canonical: 'https://data-aviator.com/where-the-data-comes-from',
    robots: 'noindex,follow',
  },
  '/intent': {
    title: 'Intent (Redirect) | Data Aviator',
    description: 'This URL is a legacy alias. The canonical page is /how-intent-data-is-gathered, which explains how Data Aviator collects and validates B2B intent signals.',
    canonical: 'https://data-aviator.com/how-intent-data-is-gathered',
    robots: 'noindex,follow',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Data Aviator',
    description: 'Read the Data Aviator privacy policy. Learn what data we collect, how we use and protect it, and the privacy choices available to you.',
    canonical: 'https://data-aviator.com/privacy-policy',
  },
  '/terms-of-service': {
    title: 'Terms of Service | Data Aviator',
    description: 'Read the Data Aviator terms of service governing your use of our B2B intent data platform, products, and website.',
    canonical: 'https://data-aviator.com/terms-of-service',
  },
  '/cookie-policy': {
    title: 'Cookie Policy | Data Aviator',
    description: 'Read the Data Aviator cookie policy. Learn which cookies and similar technologies we use, why we use them, and how to manage your preferences.',
    canonical: 'https://data-aviator.com/cookie-policy',
  },
  '/dpa': {
    title: 'Data Processing Agreement (DPA) | Data Aviator',
    description: 'Read the Data Aviator Data Processing Agreement covering how personal data is processed, protected, and handled for customers and partners.',
    canonical: 'https://data-aviator.com/dpa',
  },
  '/acceptable-use': {
    title: 'Acceptable Use Policy | Data Aviator',
    description: 'Read the Data Aviator acceptable use policy. Understand permitted and prohibited uses of our B2B intent data platform and services.',
    canonical: 'https://data-aviator.com/acceptable-use',
  },
  '/ccpa-privacy': {
    title: 'CCPA Privacy Rights (California) | Data Aviator',
    description: 'Learn your California privacy rights under the CCPA and how to exercise them with Data Aviator, including access, deletion, and do-not-sell requests.',
    canonical: 'https://data-aviator.com/ccpa-privacy',
  },
  '/gdpr-rights': {
    title: 'GDPR Data Rights | Data Aviator',
    description: 'Learn your data rights under the GDPR and how to exercise them with Data Aviator, including access, rectification, erasure, and portability.',
    canonical: 'https://data-aviator.com/gdpr-rights',
  },
  '/refund-policy': {
    title: 'Refund and Cancellation Policy | Data Aviator',
    description: 'Read the Data Aviator refund and cancellation policy. Understand eligibility, timelines, and how to request a refund or cancel your subscription.',
    canonical: 'https://data-aviator.com/refund-policy',
  },
  '/sla': {
    title: 'Service Level Agreement (SLA) | Data Aviator',
    description: 'Read the Data Aviator Service Level Agreement covering uptime commitments, support response times, and remedies for our B2B data platform.',
    canonical: 'https://data-aviator.com/sla',
  },
  '/data-broker-registration': {
    title: 'Data Broker Registration | Data Aviator',
    description: 'Data Aviator data broker registration information and the entity maintaining this website, provided for regulatory transparency and compliance.',
    canonical: 'https://data-aviator.com/data-broker-registration',
  },
  '/disclaimer': {
    title: 'Disclaimer | Data Aviator',
    description: 'Read the Data Aviator disclaimer covering the use of information on this website and the limits of any representations or warranties made.',
    canonical: 'https://data-aviator.com/disclaimer',
  },
  '/texas-privacy': {
    title: 'Texas Privacy Rights | Data Aviator',
    description: 'Learn your privacy rights under Texas law and how to exercise them with Data Aviator, including access, deletion, and opt-out requests.',
    canonical: 'https://data-aviator.com/texas-privacy',
  },
  '/texas-personal-injury-compliance': {
    title: 'Texas Personal Injury Compliance | Data Aviator',
    description: 'Read the Data Aviator Texas personal injury compliance use policy, including its purpose and the requirements that govern this data use.',
    canonical: 'https://data-aviator.com/texas-personal-injury-compliance',
  },
};

/**
 * Per-route SEO tags keyed by pathname.
 * - Self-referencing canonicals on real pages.
 * - /data-source and /intent canonical to their renamed counterparts to consolidate duplicates.
 * - /auth is noindex,follow.
 * Mounted once at the app root; safe to coexist with page-level Helmet (later tags win on dedupe).
 */
export const RouteSEO = () => {
  const { pathname } = useLocation();
  const normalized = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
  const meta = ROUTES[normalized];
  if (!meta) return null;

  const robots = meta.robots ?? DEFAULT_ROBOTS;

  return (
    <Helmet prioritizeSeoTags>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={meta.canonical} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={meta.canonical} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
    </Helmet>
  );
};

export default RouteSEO;
